import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";

const Preferences = ({
  setShowPreferences,
  baseMapLayer,
  overlayLayers,
  selectedBaseLayer,
  setSelectedBaseLayer,
  selectedOverlayLayer,
  setSelectedOverlayLayer,
}) => {
  const handleBaseLayerChange = (e) => {
    const value = e.target.value;
    const selectedBaseLayerNew =
      overlayLayers.find((layer) => layer.id === value) || baseMapLayer;
    setSelectedBaseLayer(selectedBaseLayerNew);
  };

  const handleOverlayLayerChange = (e) => {
    const value = e.target.value;
    if (value === "none") {
      setSelectedOverlayLayer(null);
    } else {
      const selectedLayerNew =
        overlayLayers.find((layer) => layer.id === value) || null;
      setSelectedOverlayLayer(selectedLayerNew);
    }
  };

  const { baseOptions, overlayOptions } = useMemo(() => {
    const base = [
      baseMapLayer,
      ...overlayLayers.map((layer) => ({
        id: layer.id,
        name: layer.name || "NOT AVAILABLE",
      })),
    ];

    const overlay = [
      { id: "none", name: "No overlay" },
      ...overlayLayers.map((layer) => ({
        id: layer.id,
        name: layer.name || "NOT AVAILABLE",
      })),
    ];

    return { baseOptions: base, overlayOptions: overlay };
  }, [overlayLayers, baseMapLayer]);

  return (
    <Paper
      elevation={3}
      sx={{
        height: "100%",
      }}
    >
      {/* Preferences Form */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Base Map */}
          <Grid item xs={12} sm={12} md={6}>
            <Typography variant="h6" sx={{ fontSize: "0.69rem" }} gutterBottom>
              BASE MAP
            </Typography>
            <FormControl fullWidth>
              <Select
                id="baseLayer"
                value={selectedBaseLayer?.id || ""}
                onChange={handleBaseLayerChange}
                displayEmpty
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mt: "2px",
                      border: "1px solid var(--theme-color)",
                      borderRadius: 0,
                      boxShadow: "none",
                      overflowX: "hidden",
                      maxWidth: "min-content",
                      maxHeight: "calc(100vh - 500px)",
                      "& .MuiMenu-list li": {
                        whiteSpace: "normal",
                        fontSize: "0.813rem",
                        "&:hover": {
                          backgroundColor: "var(--theme-bg-clr)",
                        },
                      },
                    },
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "setMaxWidth",
                        enabled: true,
                        phase: "beforeWrite",
                        requires: ["computeStyles"],
                        fn: ({ state }) => {
                          const parentWidth = state.rects.reference.width;
                          state.styles.popper.maxWidth = `${parentWidth}px`;
                        },
                      },
                    ],
                  },
                }}
                sx={{
                  "& .MuiSelect-select": {
                    padding: "4px 10px",
                    fontSize: "0.8125rem",
                    fontWeight: 400,
                    color: "var(--theme-color)",
                    borderRadius: "1px",
                  },
                  "& .MuiSelect-icon": {
                    color: "var(--theme-color)",
                    fontSize: "2rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: "transparent",
                    fontWeight: 700,
                  },
                }}
              >
                {baseOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    disabled={option.id === selectedOverlayLayer?.id}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Overlay Layers */}
          <Grid item xs={12} sm={12} md={6}>
            <Typography variant="h6" sx={{ fontSize: "0.69rem" }} gutterBottom>
              HISTORICAL OVERLAY
            </Typography>
            <FormControl fullWidth>
              <Select
                id="overlay"
                value={selectedOverlayLayer ? selectedOverlayLayer.id : "none"}
                onChange={handleOverlayLayerChange}
                displayEmpty
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mt: "2px",
                      border: "1px solid var(--theme-color)",
                      borderRadius: 0,
                      boxShadow: "none",
                      overflowX: "hidden",
                      maxWidth: "min-content",
                      maxHeight: "calc(100vh - 500px)",
                      "& .MuiMenu-list li": {
                        whiteSpace: "normal",
                        fontSize: "0.813rem",
                        "&:hover": {
                          backgroundColor: "var(--theme-bg-clr)",
                        },
                      },
                    },
                  },
                  PopperProps: {
                    modifiers: [
                      {
                        name: "setMaxWidth",
                        enabled: true,
                        phase: "beforeWrite",
                        requires: ["computeStyles"],
                        fn: ({ state }) => {
                          const parentWidth = state.rects.reference.width;
                          state.styles.popper.maxWidth = `${parentWidth}px`;
                        },
                      },
                    ],
                  },
                }}
                sx={{
                  "& .MuiSelect-select": {
                    padding: "4px 10px",
                    fontSize: "0.8125rem",
                    fontWeight: 400,
                    color: "var(--theme-color)",
                    borderRadius: "1px",
                  },
                  "& .MuiSelect-icon": {
                    color: "var(--theme-color)",
                    fontSize: "2rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--theme-color)",
                  },
                  "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: "transparent",
                    fontWeight: 700,
                  },
                }}
              >
                {overlayOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    disabled={option.id === selectedBaseLayer?.id}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default Preferences;
